import moment, {Moment, MomentInput, utc} from "moment";
import "moment-timezone";
import {Optional} from "./Optional";
import {IsoDate, IsoDateString, IsoDateTime, IsoDateTimeString, OffsetDateTime} from "../../pseudo-types/DateTypes";

export const MARKET_ZONE = "Australia/Sydney";

export function utcToZone(value: MomentInput, zone: string): Moment {
    return utc(value).tz(zone);
}

export function formatUtcToSydney(value: MomentInput, format: string): string {
    return utcToZone(value, MARKET_ZONE).format(format);
}

export function marketTime() {
    return moment().tz(MARKET_ZONE);
}

export function daysBetween(from: MomentInput, to?: MomentInput | null): number {
    const now = Optional.of(to)
                        .map(end => moment(end))
                        .orElse(moment())
                        .startOf("day");
    const then = moment(from).startOf("day");
    const duration = moment.duration(then.diff(now));
    return duration.asDays();
}

export function parseDate(from: MomentInput, pattern: string): Moment {
    return moment(from, pattern);
}

export function formatDate(from: MomentInput, format: string): string {
    return moment(from).format(format);
}

export function areSameDate(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate();
}

export class DateTimeUtil {

    public static readonly ONE_DAY_MILLIS = ((1000 * 60) * 60) * 24;
    public static readonly ONE_YEAR_MILLIS = DateTimeUtil.ONE_DAY_MILLIS * 365;

    private static readonly MONTHS = [
        "01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"
    ];

    private static readonly DAYS = [
        "01", "02", "03", "04", "05", "06", "07", "08", "09", "10",
        "11", "12", "13", "14", "15", "16", "17", "18", "19", "20",
        "21", "22", "23", "24", "25", "26", "27", "28", "29", "30",
        "31"
    ];

    public static isOlderThan(date: Date, than: Date): boolean {
        const d1 = DateTimeUtil.dateToIso(date);
        const d2 = DateTimeUtil.dateToIso(than);
        return d1.localeCompare(d2) < 0;
    }

    public static isOlderThanOrEqualTo(date: Date, than: Date): boolean {
        const d1 = DateTimeUtil.dateToIso(date);
        const d2 = DateTimeUtil.dateToIso(than);
        return d1.localeCompare(d2) <= 0;
    }

    public static oldest(d1: Date, d2: Date): Date {
        return DateTimeUtil.isOlderThan(d1, d2) ? d1 : d2;
    }

    public static isMoreRecentThan(date: Date, than: Date): boolean {
        const d1 = DateTimeUtil.dateToIso(date);
        const d2 = DateTimeUtil.dateToIso(than);
        return d1.localeCompare(d2) > 0;
    }

    public static isMoreRecentThanOrEqualTo(date: Date, than: Date): boolean {
        const d1 = DateTimeUtil.dateToIso(date);
        const d2 = DateTimeUtil.dateToIso(than);
        return d1.localeCompare(d2) >= 0;
    }

    public static mostRecent(d1: Date, d2: Date): Date {
        return DateTimeUtil.isMoreRecentThan(d1, d2) ? d1 : d2;
    }

    public static isBetween(date: Date, startDate: Date, endDate: Date): boolean {
        const moreRecentThanStart = DateTimeUtil.isMoreRecentThanOrEqualTo(date, startDate);
        const olderThanEnd = DateTimeUtil.isOlderThanOrEqualTo(date, endDate);
        return moreRecentThanStart && olderThanEnd;
    }

    public static sort(d1: IsoDate, d2: IsoDate): number;
    public static sort(d1: Date, d2: Date): number;

    public static sort(d1: Date | IsoDate, d2: Date | IsoDate): number {
        if (d1 instanceof Date && d2 instanceof Date) {
            return d1.getTime() - d2.getTime();
        } else if (typeof d1 === "string" && typeof d2 === "string") {
            return d1.localeCompare(d2);
        } else {
            throw Error(`Unable to compare ${typeof d1} with ${typeof d2}`);
        }
    }

    public static createDate(date: MomentInput): Date {
        return moment.utc(date).toDate();
    }

    public static isWeekend(date: Date): boolean {
        const dayNum = date.getDay();
        return dayNum === 0 || dayNum === 6;
    }

    public static momentToIsoDate(input: Moment): IsoDate {
        return IsoDateString(input.format("YYYY-MM-DD"));
    }

    public static offsetDateTime(input: MomentInput): OffsetDateTime {
        return moment(input).format("YYYY-MM-DDTHH:mm:ssZ") as OffsetDateTime;
    }

    public static dateToIso(date: Date): IsoDate {
        const year = date.getFullYear();
        const month = DateTimeUtil.MONTHS[date.getMonth()];
        const day = DateTimeUtil.DAYS[date.getDate() - 1];
        const iso = `${year}-${month}-${day}`;
        return iso as IsoDate;
    }

    public static isoDate(dateTime: IsoDateTime): IsoDate {
        const m = moment(dateTime);
        return DateTimeUtil.momentToIsoDate(m);
    }

    public static isoToday(): IsoDate {
        return DateTimeUtil.dateToIso(new Date());
    }

    public static isoNow(): IsoDateTime {
        return IsoDateTimeString(moment().format("YYYY-MM-DDTHH:mm:ss"));
    }

    public static offsetNow(): OffsetDateTime {
        return DateTimeUtil.offsetDateTime(moment());
    }

    public static daysBetween(d1: Date, d2: Date): number {
        const start = moment(d1).startOf("day");
        const end = moment(d2).startOf("day");

        const duration = moment.duration(start.diff(end));

        const days = Math.round(duration.asDays());
        return Math.abs(days);
    }

    public static preciseDaysBetween(d1: Date, d2: Date): number {
        const start = moment(d1);
        const end = moment(d2);

        const duration = moment.duration(start.diff(end));
        return Math.abs(duration.asDays());
    }
}
